import type {ChangeEvent, ReactElement} from 'react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {SMP_AI_CAPTION_PROMPT_SUGGESTION_LANG_LINES, SocialPlatform, SocialPostCaptionOverwriteEvent} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import useOutsideClick from '@Hooks/useOutsideClick';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import {useCommonCaption, useHasCommonCaption, usePostPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import {
  updateCommonCaption,
  updateHasCommonCaption,
  updatePublishingParamAllCaptions,
  updatePublishingParamCaption,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {Text, TextSize, TextType} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import {BLURABLE_CLASS_IN_AI_CAPTION_MODE, captionCharacterLimits, CaptionErrorTypes, PUBLISH_PARAM_HANDLER_CLASS} from '@Components/social-media-wizard/social-media-wizard.types';
import {
  getPostId,
  useAreCaptionsBeingAutoGenerated,
  useIsSocialMediaWizardAiCaptionsEnabled,
  usePlatformSelectionforAccount,
  useWizardAccountsToPublishTo,
  useWizardCaptionErrors,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {AiButton} from '@Components/ai-button';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {useModalData} from '@Components/modal/hooks/useModalData';
import {IntroPopup} from '@Components/intro-popup';
import {getAssetUrl} from '@Utils/s3.util';
import {GA4EventName} from '@Libraries/ga-events';
import {isUserPremium} from '@Libraries/user.library';
import {AnimatePresence, motion} from 'framer-motion';
import useAutoCaptionGlobal from '@Hooks/auto-captions/useAutoCaptionGlobal';
import useHasSeenAiCaptionIntroPopup from '@Hooks/auto-captions/useHasSeenAiCaptionIntroPopup';
import {EmojiPickerInput} from '@Components/emoji-picker-input';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import styles from './social-post-caption.module.scss';
import {useAppDispatch} from '@/hooks';
import {AiTextPromptBox} from '@Components/ai-text-prompt-box';
import type {AiTextPromptBoxProps} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant, PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {
  createAlterFunctionWithContext,
  createGenerateCaptionsFunctionWithContext,
  createUseFunctionWithContext,
  getAlterationLoadingText,
  getPromptBoxTitles,
  requestRecentCaptions,
  requestReuseCaption,
} from '@Libraries/auto-caption-library';
import useSelectCaption from '@Hooks/auto-captions/useSelectCaption';
import {openAiTextPromptBoxModal} from '@Panels/ai-text-prompt-box-panel';
import {getRandomizedLoadingCaption} from '@Components/social-media-wizard/components/social-post-caption/social-post-caption.types';

interface SocialPostCaptionProps {
  account: SocialAccount;
}

const WAIT_TIME_BEFORE_AI_CAPTION_BUTTON_PULSATES_SECS = 10000; // 10 seconds
const WAIT_TIME_BEFORE_AI_CAPTION_REMINDER_POPOVER_IS_VISIBLE = 20000; // 20 seconds

export function SocialPostCaption({account}: SocialPostCaptionProps): ReactElement<SocialPostCaptionProps> | null {
  const [isShowingConfirmation, setIsShowingConfirmation] = useState(false);
  const [isEmojiSelectorVisible, setIsEmojiSelectorVisible] = useState(false);
  const [isCountErrorVisible, setIsCountErrorVisible] = useState(false);
  const [isHashtagErrorVisible, setIsHashtagErrorVisible] = useState(false);
  const [isCaptionChangeMultiCharacter, setIsCaptionChangeMultiCharacter] = useState(false);
  const dispatch = useAppDispatch();
  const publishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const commonCaption = useCommonCaption();
  const hasCommonCaption = useHasCommonCaption();
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const textAreaContainerRef = useRef<HTMLDivElement>(null);
  const captionErrors = useWizardCaptionErrors();
  const accountIdsToPublishTo = useWizardAccountsToPublishTo();
  const {inAutoCaptionMode, setInAutoCaptionMode, closeAutoCaptionMode} = useAutoCaptionGlobal();
  const {hasSeenIntroPopup: userHasSeenAiCaptionPopup, setHasSeenIntroPopup, hasSeenReminderPopup, setHasSeenReminderPopup} = useHasSeenAiCaptionIntroPopup();
  const isMobile = useIsMobileWidth();
  const autoCaptionsModalData = useModalData(MODALS_IDS.AUTO_CAPTIONS_MODAL);
  const CAPTION_TEXT_BOX_HEIGHT = 220;
  const CAPTION_CONTAINER_TEXTBOX_HEIGHT = 245;
  const AUTO_CAPTION_BOX_HEIGHT = 280;
  const socialPostCaptionContainerRef = useRef<HTMLDivElement>(null);
  const aiCaptionButtonRef = useRef<HTMLButtonElement>(null);
  const [isAiCaptionButtonPulsating, setIsAiCaptionButtonPulsating] = useState<boolean>(false);
  const [isVisibleAiCaptionReminderPopup, setIsVisibleAiCaptionReminderPopup] = useState<boolean>(false);
  const isSocialMediaWizardAiCaptionsEnabled = useIsSocialMediaWizardAiCaptionsEnabled();
  const aiCaptionsPulseTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const aiCaptionReminderPopupTimeout = useRef<NodeJS.Timeout>(undefined);
  const platformSelection = usePlatformSelectionforAccount(account.id);
  const postId = getPostId();
  const autoCaptionRef = useRef<HTMLDivElement>(null);
  const {chooseCaption} = useSelectCaption();
  const areCaptionsBeingAutoGenerated = useAreCaptionsBeingAutoGenerated();
  const [autoGenerationLoadingCaption, setAutoGenerationLoadingCaption] = useState<string>('');

  const getCurrentCaption = (): string => {
    if (hasCommonCaption && commonCaption) {
      return commonCaption;
    }

    if (!publishingParamsForAccount?.caption && textAreaRef.current) {
      return '';
    }

    return publishingParamsForAccount?.caption || '';
  };

  const captionCharCount = getCurrentCaption().length;

  useEffect(() => {
    if (!isMobile && autoCaptionsModalData?.isOpen) {
      dispatch(closeModal(MODALS_IDS.AUTO_CAPTIONS_MODAL));
    }

    if (!postId) {
      return;
    }

    if (isMobile && !autoCaptionsModalData?.isOpen && !!inAutoCaptionMode.accountId) {
      openAiTextPromptBoxModal({
        optionsToUse: [PromptBoxConfigOptionType.TONE, PromptBoxConfigOptionType.LENGTH, PromptBoxConfigOptionType.HASHTAGS],
        onBack: closeAutoCaptionMode,
        useContent: createUseFunctionWithContext(account.id, postId),
        generateContent: createGenerateCaptionsFunctionWithContext(account.id, postId),
        reuseContent: requestReuseCaption,
        getRecents: requestRecentCaptions,
        variant: AiPromptBoxVariant.MONOCONTENT,
        titles: getPromptBoxTitles(isMobile),
        alterationLoadingTexts: getAlterationLoadingText(),
        isInMobileMode: isMobile,
        alterContent: createAlterFunctionWithContext(account.id, postId),
        ref: autoCaptionRef,
        mainPlaceholder: window.i18next.t('pmwjs_caption_about_ellipsis'),
        onContentSelected: chooseCaption,
        contentClassName: '',
        innerContentClassName: '',
        sectionsContainerClass: '',
        promptboxContainerClass: '',
      });
    }
  }, [isMobile, autoCaptionsModalData, inAutoCaptionMode, postId]);

  useEffect(() => {
    if (inAutoCaptionMode.accountId && !isMobile) {
      blurAllBlurableElementsInTheDocument();
    } else {
      removeBlurFromAllBlurredElements();
    }
  }, [inAutoCaptionMode.accountId]);

  useEffect(() => {
    const loadingStateCaption = getRandomizedLoadingCaption();
    setAutoGenerationLoadingCaption(loadingStateCaption);
    const captionOverwriteCallback = (event: Event): void => {
      triggerTextAreaAnimation();
    };

    window.addEventListener(SocialPostCaptionOverwriteEvent, captionOverwriteCallback);

    return () => {
      window.removeEventListener(SocialPostCaptionOverwriteEvent, captionOverwriteCallback);
    };
  }, []);

  useEffect(() => {
    if (captionErrors !== undefined) {
      if (captionErrors[account.id] !== CaptionErrorTypes.NO_ERROR) {
        handleError();
      } else {
        setIsCountErrorVisible(false);
      }
    }
  }, [captionErrors]);

  const hasShortCaptionLimit = (accountType: SocialPlatform): boolean => {
    // Rather than this, can we not check if the captionCharacterLimits[accountType] <= 500?
    // 500 can be an exported constant from social-media-wizard.types.ts
    switch (accountType) {
      case SocialPlatform.THREADS:
      case SocialPlatform.TWITTER:
        return true;
      default:
        return false;
    }
  };

  const getCaptionPlaceholder = (): string => {
    switch (account.type) {
      case SocialPlatform.FACEBOOK:
      case SocialPlatform.FACEBOOK_PROFILE:
      case SocialPlatform.FACEBOOK_PAGE:
      case SocialPlatform.FACEBOOK_GROUP:
        return window.i18next.t('pmwjs_wizard_caption_placeholder_facebook');
      case SocialPlatform.TWITTER:
        return window.i18next.t('pmwjs_whats_happening');
      case SocialPlatform.LINKEDIN:
      case SocialPlatform.LINKEDIN_PAGE:
      case SocialPlatform.LINKEDIN_PROFILE:
        return window.i18next.t('pmwjs_what_to_talk_about');
      case SocialPlatform.TIKTOK:
        return window.i18next.t('pmwjs_describe_post_hashtags');
      case SocialPlatform.YOUTUBE:
        return window.i18next.t('pmwjs_add_a_caption_ellipses');
      case SocialPlatform.INSTAGRAM:
      case SocialPlatform.THREADS:
        return window.i18next.t('pmwjs_write_a_caption');
      case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
        return window.i18next.t('pmwjs_post_update_for_x', {accountName: account.name});
      default:
        return '';
    }
  };

  const shouldShowIntroPopup = (): boolean => {
    if (isMobile) {
      return false;
    }

    if (userHasSeenAiCaptionPopup) {
      return false;
    }

    return accountIdsToPublishTo[0] === account.id;
  };

  const shouldShowReminderPopup = (): boolean => {
    if (!userHasSeenAiCaptionPopup) {
      return false;
    }

    if (hasSeenReminderPopup) {
      return false;
    }

    return accountIdsToPublishTo[0] === account.id;
  };

  const blurAllBlurableElementsInTheDocument = (): void => {
    document.querySelectorAll(`.${BLURABLE_CLASS_IN_AI_CAPTION_MODE}`).forEach((el) => {
      el.classList.add(styles.blurElementsInAiCaptionMode);
    });

    document.querySelectorAll(`.${PUBLISH_PARAM_HANDLER_CLASS}`).forEach((el) => {
      const currentAccountId = parseInt(el.getAttribute('data-id-account') ?? '', 10);
      if (inAutoCaptionMode.accountId === currentAccountId) {
        el.classList.remove(styles.blurElementsInAiCaptionMode);
      } else {
        el.classList.add(styles.blurElementsInAiCaptionMode);
      }
    });
  };

  const removeBlurFromAllBlurredElements = (): void => {
    document.querySelectorAll(`.${BLURABLE_CLASS_IN_AI_CAPTION_MODE}`).forEach((el) => {
      el.classList.remove(styles.blurElementsInAiCaptionMode);
    });
    document.querySelectorAll(`.${PUBLISH_PARAM_HANDLER_CLASS}`).forEach((el) => {
      el.classList.remove(styles.blurElementsInAiCaptionMode);
    });
  };

  const onAiCaptionClick = (viaOnboardingModal = false): void => {
    if (!postId) {
      return;
    }

    setInAutoCaptionMode({accountId: account.id});
    abortPulsatingButtonTimer();

    if (isMobile) {
      setIsAiCaptionButtonPulsating(false);
      openAiTextPromptBoxModal({
        optionsToUse: [PromptBoxConfigOptionType.TONE, PromptBoxConfigOptionType.LENGTH, PromptBoxConfigOptionType.HASHTAGS],
        onBack: closeAutoCaptionMode,
        useContent: createUseFunctionWithContext(account.id, postId),
        generateContent: createGenerateCaptionsFunctionWithContext(account.id, postId),
        reuseContent: requestReuseCaption,
        getRecents: requestRecentCaptions,
        variant: AiPromptBoxVariant.MONOCONTENT,
        titles: getPromptBoxTitles(isMobile),
        alterationLoadingTexts: getAlterationLoadingText(),
        isInMobileMode: isMobile,
        alterContent: createAlterFunctionWithContext(account.id, postId),
        ref: autoCaptionRef,
        mainPlaceholder: window.i18next.t('pmwjs_caption_about_ellipsis'),
        onContentSelected: chooseCaption,
        contentClassName: '',
        innerContentClassName: '',
        sectionsContainerClass: '',
        promptboxContainerClass: '',
      });
    } else {
      if (!userHasSeenAiCaptionPopup) {
        setHasSeenIntroPopup(true);
      }

      if (userHasSeenAiCaptionPopup && !hasSeenReminderPopup && isVisibleAiCaptionReminderPopup) {
        onSeenReminderPopup();
      }
    }

    if (viaOnboardingModal) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_ONBOARDING_TRYNOW);
    } else {
      if (isAiCaptionButtonPulsating) {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_REMINDER_VISIBLE_CLICK);
      }

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_CLICKED);
    }
  };

  const afterCloseIntroPopup = (): void => {
    setHasSeenIntroPopup(true);
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_ONBOARDING_LATER);
  };

  const onEmojiPickerOutsideClick = (): void => {
    setIsEmojiSelectorVisible(false);
  };

  const startPulsatingButtonTimer = (): void => {
    if (!shouldShowReminderPopup()) {
      return;
    }

    aiCaptionsPulseTimeoutRef.current = setTimeout(() => {
      setIsAiCaptionButtonPulsating(true);
    }, WAIT_TIME_BEFORE_AI_CAPTION_BUTTON_PULSATES_SECS);

    if (!isMobile) {
      aiCaptionReminderPopupTimeout.current = setTimeout(() => {
        setIsVisibleAiCaptionReminderPopup(true);
        setHasSeenReminderPopup(true);
      }, WAIT_TIME_BEFORE_AI_CAPTION_REMINDER_POPOVER_IS_VISIBLE);
    }
  };

  const abortPulsatingButtonTimer = (): void => {
    if (aiCaptionsPulseTimeoutRef.current) {
      clearTimeout(aiCaptionsPulseTimeoutRef.current);
    }

    if (aiCaptionReminderPopupTimeout.current) {
      clearTimeout(aiCaptionReminderPopupTimeout.current);
    }

    setIsAiCaptionButtonPulsating(false);
    setIsVisibleAiCaptionReminderPopup(false);
  };

  const onEmojiSelected = (newCaption: string): void => {
    if (hasCommonCaption) {
      dispatch(updateCommonCaption(newCaption));
      dispatch(updatePublishingParamAllCaptions({caption: newCaption, isMultiCharacterChange: false}));
    } else {
      dispatch(
        updatePublishingParamCaption({
          idSocialMediaAccount: account.id,
          caption: newCaption,
          isMultiCharacterChange: false,
        })
      );
    }
  };

  useOutsideClick(emojiPickerRef, onEmojiPickerOutsideClick, iconRef.current, () => {
    return isEmojiSelectorVisible;
  });

  const onUseCaptionChange = (isChecked: boolean): void => {
    if (isChecked) {
      setIsShowingConfirmation(true);
    } else {
      dispatch(updateHasCommonCaption(false));
    }
  };

  const enableOverwriteCaptions = (): void => {
    const unifiedCaption = textAreaRef.current?.value;

    if (!hasCommonCaption) {
      dispatch(updateHasCommonCaption(true));
    }

    if (unifiedCaption) {
      dispatch(updateCommonCaption(unifiedCaption));
      dispatch(updatePublishingParamAllCaptions({caption: unifiedCaption, isMultiCharacterChange: true}));
    }
  };

  const triggerTextAreaAnimation = (): void => {
    if (textAreaContainerRef?.current) {
      textAreaContainerRef.current.classList.add(styles.animationFocus);

      setTimeout(() => {
        if (textAreaContainerRef?.current) {
          textAreaContainerRef.current.classList.remove(styles.animationFocus);
        }
      }, 700);
    }
  };

  const onConfirmOverwriteCaptions = (): void => {
    window.dispatchEvent(new CustomEvent(SocialPostCaptionOverwriteEvent));
    dispatch(updateHasCommonCaption(true));
    enableOverwriteCaptions();
    setIsShowingConfirmation(false);
  };

  const onCancelOverwriteCaptions = (): void => {
    setIsShowingConfirmation(false);
  };

  const openUpsellDialogForAutoCaption = (): void => {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AUTO_CAPTION).then(() => {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.SOCIALWIZARD_AICAPTIONS_UPSELLING);
    });
  };

  const handleError = (): void => {
    if (captionErrors[account.id] === CaptionErrorTypes.COUNT_ERROR) {
      setIsHashtagErrorVisible(false);
      setIsCountErrorVisible(true);
    }

    if (captionErrors[account.id] === CaptionErrorTypes.HASHTAG_ERROR) {
      setIsHashtagErrorVisible(true);
      setIsCountErrorVisible(false);
    }
  };

  const onCaptionChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    if (e.target) {
      const caption: string = e.target.value;

      if (hasCommonCaption) {
        dispatch(updateCommonCaption(caption));
        dispatch(
          updatePublishingParamAllCaptions({
            caption,
            isMultiCharacterChange: isCaptionChangeMultiCharacter,
          })
        );
      } else {
        dispatch(
          updatePublishingParamCaption({
            idSocialMediaAccount: account.id,
            caption,
            isMultiCharacterChange: isCaptionChangeMultiCharacter,
          })
        );
      }
    }

    if (isCaptionChangeMultiCharacter) {
      setIsCaptionChangeMultiCharacter(false);
    }
  };

  const onCaptionPaste = (): void => {
    setIsCaptionChangeMultiCharacter(true);
  };

  const isCurrentCaptionInAutoCaptionMode = (): boolean => {
    return inAutoCaptionMode.accountId === account.id;
  };

  const autoCaptionsButton = (): ReactElement | null => {
    if (!isSocialMediaWizardAiCaptionsEnabled) {
      return null;
    }

    if (!isUserPremium()) {
      return getPremiumUpsellAiCaptionsButton();
    }

    return getDefaultAutoCaptionButtonWithIntroPopup();
  };

  const isCurrentCaptionInAIModeAndNotOnMobile = useMemo((): boolean => {
    return isCurrentCaptionInAutoCaptionMode() && !isMobile;
  }, [isMobile, inAutoCaptionMode.accountId, account.id]);

  const onSeenReminderPopup = (): void => {
    setHasSeenReminderPopup(true);
    setIsAiCaptionButtonPulsating(false);
    setIsVisibleAiCaptionReminderPopup(false);
  };

  const commonCaptionCheckboxConfirmation = useMemo((): ReactElement | null => {
    if (accountIdsToPublishTo.length > 1) {
      return (
        <>
          <LabelledCheckbox
            className={`${styles.useCaptionLabel} ${isShowingConfirmation ? '_hidden' : ''}`}
            onChange={onUseCaptionChange}
            isChecked={hasCommonCaption}
            label={window.i18next.t('pmwjs_use_caption_all_posts')}
          />
          <div className={`flex-v-row ${styles.captionConfirmationBox} ${!isShowingConfirmation ? '_hidden' : ''}`}>
            <div className="flex-row-align-center spacing-m-b-3 spacing-m-t-2">
              <Icon icon="icon-info" className={`color-warning-dark ${styles.infoIconSpacer}`} type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
              <Text val={window.i18next.t('pmwjs_are_you_sure_use_caption')} size={TextSize.XSMALL} className={styles.captionConfirmationText} />
            </div>
            <div className="flex-h-row flex-content-end">
              <Button type={Type.SECONDARY} size={Size.SMALL} onClick={onCancelOverwriteCaptions} text={window.i18next.t('pmwjs_cancel')} customClasses="spacing-m-r-2" />
              <Button type={Type.SECONDARY} size={Size.SMALL} onClick={onConfirmOverwriteCaptions} text={window.i18next.t('pmwjs_yes')} />
            </div>
          </div>
        </>
      );
    }

    return null;
  }, [isShowingConfirmation, hasCommonCaption]);

  const getCountError = (): ReactElement => {
    const twitterVerifiedCount = 10000;
    const characterLimit = account.isVerifiedTwitterUser ? twitterVerifiedCount : captionCharacterLimits[account.type];

    return (
      <div className="flex-row-align-center spacing-m-t-2">
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="spacing-m-r-2 content-danger" />
        <Text className="content-danger" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_caption_long_error', {limit: characterLimit})} />
      </div>
    );
  };

  const getCharCounter = (): ReactElement | null => {
    if (!hasShortCaptionLimit(account.type)) {
      return null;
    }

    const characterLimit = captionCharacterLimits[account.type];

    return (
      <Text
        className={`${styles.charCounter} ${isCountErrorVisible ? styles.captionError : ''}`}
        size={TextSize.XXSMALL}
        type={TextType.BODY}
        val={`${captionCharCount} / ${characterLimit}`}
      />
    );
  };

  const getHashtagError = (): ReactElement => {
    return (
      <div className="flex-row-align-center spacing-m-t-2">
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="spacing-m-r-2 content-danger" />
        <Text className="content-danger" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_hashtag_error')} />
      </div>
    );
  };

  const emojiPicker = useMemo((): ReactElement | null => {
    return (
      <div ref={emojiPickerRef} className={`${styles.emojiSelector} ${isEmojiSelectorVisible ? styles.visible : ''}`}>
        <EmojiPickerInput
          textAreaRef={textAreaRef}
          onEmojiSelectCallback={(newText) => {
            onEmojiSelected(newText);
          }}
        />
      </div>
    );
  }, [isEmojiSelectorVisible]);

  const getPremiumUpsellAiCaptionsButton = (): ReactElement => {
    return (
      <div className={styles.autoCaptionButtonContainer}>
        <AiButton text={window.i18next.t('pmwjs_ai_captions')} variant={AIButtonVariant.PREMIUM} size={Size.SMALL} onClick={openUpsellDialogForAutoCaption} />
      </div>
    );
  };

  const getAutoCaptionIntroPopup = (): ReactElement | null => {
    if (!shouldShowIntroPopup()) {
      return null;
    }

    return (
      <IntroPopup
        userHasSeenCallbackOnClose={afterCloseIntroPopup}
        title={window.i18next.t('pmwjs_say_goodbye_to_writers_block')}
        content={window.i18next.t('pmwjs_ai_caption_intro_text')}
        assetUrl={getAssetUrl('ai-captions-illustration.png')}
        leftOffset={70}
        shouldFlip={false}
        shouldShift
        primaryFooterAction={
          <Button
            text={window.i18next.t('pmwjs_try_now')}
            type={Type.PRIMARY}
            size={Size.XSMALL}
            customClasses={styles.secondaryGradeintColorBackground}
            onClick={() => {
              onAiCaptionClick(true);
            }}
          />
        }
      />
    );
  };

  const getAiCaptionReminderPopup = (): ReactElement | null => {
    if (!isVisibleAiCaptionReminderPopup) {
      return null;
    }

    return (
      <IntroPopup
        userHasSeenCallbackOnClose={onSeenReminderPopup}
        title={window.i18next.t('pmwjs_let_ai_do_the_writing_for_you')}
        content={window.i18next.t('pmwjs_ai_caption_reminder_popup_text')}
        assetUrl={getAssetUrl('ai-caption-reminder-graphic.png')}
        mediaWidth={320}
        leftOffset={70}
        hasSmoothFadeIn
        shouldShift
        shouldFlip={false}
      />
    );
  };

  const getDefaultAutoCaptionButtonWithIntroPopup = (): ReactElement => {
    return (
      <div className={styles.autoCaptionButtonContainer}>
        <AiButton
          buttonRef={aiCaptionButtonRef}
          text={window.i18next.t('pmwjs_ai_captions')}
          onClick={onAiCaptionClick as VoidFunction}
          variant={AIButtonVariant.SUBDUED}
          isPulsating={isAiCaptionButtonPulsating}
          size={Size.SMALL}
          icon="icon-ai-caption"
        />
        {getAutoCaptionIntroPopup()}
        {getAiCaptionReminderPopup()}
      </div>
    );
  };

  const renderAutoCaption = (): ReactElement | null => {
    const accountId = inAutoCaptionMode.accountId;

    if (!accountId || !postId) {
      return null;
    }

    const opts: AiTextPromptBoxProps = {
      optionsToUse: [PromptBoxConfigOptionType.TONE, PromptBoxConfigOptionType.LENGTH, PromptBoxConfigOptionType.HASHTAGS],
      onBack: closeAutoCaptionMode,
      useContent: createUseFunctionWithContext(accountId, postId),
      generateContent: createGenerateCaptionsFunctionWithContext(accountId, postId),
      reuseContent: requestReuseCaption,
      getRecents: requestRecentCaptions,
      variant: AiPromptBoxVariant.MONOCONTENT,
      titles: getPromptBoxTitles(isMobile),
      alterationLoadingTexts: getAlterationLoadingText(),
      isInMobileMode: isMobile,
      alterContent: createAlterFunctionWithContext(accountId, postId),
      ref: autoCaptionRef,
      mainPlaceholder: window.i18next.t('pmwjs_caption_about_ellipsis'),
      onContentSelected: chooseCaption,
      internationalizedPromptSuggestions: SMP_AI_CAPTION_PROMPT_SUGGESTION_LANG_LINES,
      contentClassName: '',
      innerContentClassName: '',
      sectionsContainerClass: '',
      promptboxContainerClass: '',
    };

    return (
      <motion.div
        key="auto-caption-animated-container"
        initial={{opacity: 0, height: CAPTION_TEXT_BOX_HEIGHT}}
        animate={{
          opacity: 1,
          transition: {
            opacity: {duration: 0.3},
          },
        }}
        exit={{
          opacity: 0,
          transition: {
            opacity: {duration: 0.3},
          },
        }}
        className={styles.captionContainer}
      >
        <AiTextPromptBox {...opts} />
      </motion.div>
    );
  };

  const renderSocialPostCaption = (): ReactElement => {
    return (
      <motion.div
        key="social-caption-animated-container"
        initial={{opacity: 0, height: AUTO_CAPTION_BOX_HEIGHT}}
        animate={{
          opacity: 1,
          height: CAPTION_TEXT_BOX_HEIGHT,
          transition: {
            opacity: {duration: 0.3},
            height: {duration: 0.3},
          },
        }}
        exit={{
          opacity: 0,
          height: AUTO_CAPTION_BOX_HEIGHT,
          transition: {
            opacity: {duration: 0.3},
            height: {duration: 1},
          },
        }}
        className={styles.captionContainer}
      >
        <div
          ref={textAreaContainerRef}
          className={`flex-v-row flex-content-space-between _full-height ${styles.textAreaContainer} ${isCountErrorVisible ? styles.captionError : ''}`}
          onFocus={startPulsatingButtonTimer}
          onBlur={abortPulsatingButtonTimer}
        >
          <textarea
            onPaste={onCaptionPaste}
            ref={textAreaRef}
            className={`${styles.textArea} ${isCountErrorVisible ? styles.captionError : ''}`}
            placeholder={getCaptionPlaceholder()}
            onChange={onCaptionChange}
            value={getCurrentCaption()}
          />
          <div className={`flex-row-justify-between flex-align-items-center spacing-p-2 ${styles.textAreaContainerFooter}`}>
            {emojiPicker}
            <div className={styles.charCounterAndAIButtonContainer}>
              {getCharCounter()}
              {autoCaptionsButton()}
            </div>
          </div>
        </div>
        {commonCaptionCheckboxConfirmation}
        {isCountErrorVisible ? getCountError() : null}
        {isHashtagErrorVisible ? getHashtagError() : null}
      </motion.div>
    );
  };

  const getEmojiFromLangString = (string?: string): string | undefined => {
    return string?.slice(0, 2);
  };

  const getTextFromLangString = (string?: string): string | undefined => {
    return string?.slice(2);
  };

  const renderAutoCaptionGeneration = (): ReactElement => {
    return (
      <div className={`${styles.title} ${styles.aiLoader}`}>
        <div>
          <span>{getEmojiFromLangString(autoGenerationLoadingCaption)}</span>
          <span className={styles.aiFontColor}>{getTextFromLangString(autoGenerationLoadingCaption)}</span>
        </div>
      </div>
    ) as ReactElement;
  };

  const getContent = (): ReactElement | null => {
    if (areCaptionsBeingAutoGenerated) {
      return renderAutoCaptionGeneration();
    }

    if (isCurrentCaptionInAIModeAndNotOnMobile) {
      return renderAutoCaption();
    } else {
      return renderSocialPostCaption();
    }
  };

  return (
    <div
      ref={socialPostCaptionContainerRef}
      className={`${styles.socialCaptionContainer} social-caption-container ${isCurrentCaptionInAIModeAndNotOnMobile ? '-in-ai-mode' : ''}`}
      style={{height: isCurrentCaptionInAIModeAndNotOnMobile ? AUTO_CAPTION_BOX_HEIGHT : CAPTION_CONTAINER_TEXTBOX_HEIGHT}}
      key={`${account.id} ${platformSelection}`}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {getContent()}
      </AnimatePresence>
    </div>
  );
}

export default React.memo(SocialPostCaption);
